import React, { useState, useEffect } from "react";
import ExcelJS from "exceljs";
import FileSaver from "file-saver";
import { motion } from "framer-motion";
import {
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaTruckFast,
  FaCarSide,
  FaToggleOn,
  FaToggleOff,
  FaCalendar,
  FaSearchengin,
  FaShip,
} from "react-icons/fa6";
import moment from "moment";
import api from "../../services/api";

const CommissionReport = ({
  rentals,
  transfers,
  yachtBookings,
  partner,
  onRentalsUpdate,
  onTransfersUpdate,
  onYachtBookingsUpdate,
}) => {
  console.log("transfers", transfers);
  console.log("yacht bookings", yachtBookings);
  const [activeTab, setActiveTab] = useState("yachts"); // 'rentals', 'transfers', or 'yachts'
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [searchQuery, setSearchQuery] = useState("");
  const adminToken = localStorage.getItem("adminToken");
  const partnerToken = localStorage.getItem("partnerToken");

  // Filter rentals based on search query
  const filteredRentals = rentals.filter(
    (rental) =>
      rental.customer.firstName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      rental.customer.lastName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      rental.carName.toLowerCase().includes(searchQuery.toLowerCase())
  );
  // Filter transfers based on search query
  const filteredTransfers = transfers
    ? transfers.filter(
        (transfer) =>
          transfer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transfer.surname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transfer.pickupLocation
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          transfer.dropoffLocation
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase())
      )
    : [];
    
  // Filter yacht bookings based on search query
  const filteredYachtBookings = yachtBookings
    ? yachtBookings.filter(
        (booking) =>
          booking.customer?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          booking.customer?.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          booking.yachtName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          booking.cruiseType?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // Get rentals for the selected month
  const monthlyRentals = filteredRentals.filter(
    (rental) =>
      moment(rental.createdAt).format("YYYY-MM") ===
      selectedMonth.format("YYYY-MM")
  );
  // Get transfers for the selected month
  const monthlyTransfers = filteredTransfers.filter(
    (transfer) =>
      moment(transfer.createdAt).format("YYYY-MM") ===
      selectedMonth.format("YYYY-MM")
  );
  
  // Get yacht bookings for the selected month
  const monthlyYachtBookings = filteredYachtBookings.filter(
    (booking) =>
      moment(booking.createdAt).format("YYYY-MM") ===
      selectedMonth.format("YYYY-MM")
  );
  // Calculate statistics based on active tab
  const stats = 
    activeTab === "rentals" 
      ? {
          totalCount: monthlyRentals.length,
          totalCommission: monthlyRentals.reduce(
            (sum, rental) => sum + (rental.commissionAmount || 0),
            0
          ),
          totalBookingValue: monthlyRentals.reduce(
            (sum, rental) => sum + rental.totalPrice,
            0
          ),
          averageCommission: monthlyRentals.length
            ? monthlyRentals.reduce(
                (sum, rental) => sum + (rental.commissionAmount || 0),
                0
              ) / monthlyRentals.length
            : 0,
        }
      : activeTab === "transfers" 
      ? {
          totalCount: monthlyTransfers.length,
          totalCommission: monthlyTransfers.reduce(
            (sum, transfer) => sum + (transfer.partnerCommission || 0),
            0
          ),
          totalBookingValue: monthlyTransfers.reduce(
            (sum, transfer) => sum + (transfer.priceTotal || 0),
            0
          ),
          averageCommission: monthlyTransfers.length
            ? monthlyTransfers.reduce(
                (sum, transfer) => sum + (transfer.partnerCommission || 0),
                0
              ) / monthlyTransfers.length
            : 0,
        }
      : {
          totalCount: monthlyYachtBookings.length,
          totalCommission: monthlyYachtBookings.reduce(
            (sum, booking) => sum + (booking.commissionAmount || 0),
            0
          ),
          totalBookingValue: monthlyYachtBookings.reduce(
            (sum, booking) => sum + (booking.calculatedPrice || 0),
            0
          ),
          averageCommission: monthlyYachtBookings.length
            ? monthlyYachtBookings.reduce(
                (sum, booking) => sum + (booking.commissionAmount || 0),
                0
              ) / monthlyYachtBookings.length
            : 0,
        };
  const handlePrevMonth = () => {
    setSelectedMonth((prev) => prev.clone().subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setSelectedMonth((prev) => prev.clone().add(1, "month"));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery("");
  };

  // Handle toggling commission payment status for yacht bookings
  const handleYachtBookingCommissionPaymentToggle = async (
    bookingId,
    currentStatus
  ) => {
    try {
      await api.yachtBookings.updateCommissionPayment(
        bookingId,
        !currentStatus
      );
      // Call the parent's update function to refresh data
      onYachtBookingsUpdate();
    } catch (error) {
      console.error(
        "Error updating yacht booking commission payment status:",
        error
      );
    }
  };
  // Pay all unpaid commissions for the selected month based on active tab
  const handlePayAllMonth = async () => {
    try {
      if (activeTab === "yachts") {
        const unpaidYachtBookings = monthlyYachtBookings.filter(
          (booking) => !booking.commissionPaid
        );
        await Promise.all(
          unpaidYachtBookings.map((booking) =>
            api.yachtBookings.updateCommissionPayment(booking._id, true)
          )
        );
        onYachtBookingsUpdate();
      }
    } catch (error) {
      console.error("Error updating commission payments:", error);
    }
  };  const downloadReport = () => {
    // Create a new Excel workbook
    const workbook = new ExcelJS.Workbook();    if (activeTab === "rentals") {
      // RENTAL REPORT WORKSHEET
      const worksheet = workbook.addWorksheet("Rental Commission Report");

      // Set column widths for better readability
      worksheet.columns = [
        { header: "Date", key: "date", width: 14 },
        { header: "Customer", key: "customer", width: 25 },
        { header: "Car", key: "car", width: 25 },
        { header: "Rental Duration", key: "duration", width: 16 },
        { header: "Total Price", key: "price", width: 14 },
        { header: "Commission", key: "commission", width: 14 },
        { header: "Status", key: "status", width: 10 },
      ];

      // Add title and metadata with styling
      const titleRow = worksheet.addRow([
        `Rental Commission Report for ${partner.firstName} ${partner.lastName}`,
      ]);
      titleRow.height = 30;
      titleRow.font = { size: 16, bold: true };
      worksheet.mergeCells(`A1:G1`);
      titleRow.alignment = { horizontal: "center", vertical: "middle" };

      const monthRow = worksheet.addRow([
        `Month: ${selectedMonth.format("MMMM YYYY")}`,
      ]);
      monthRow.font = { size: 12, bold: true };
      worksheet.mergeCells(`A2:G2`);
      monthRow.alignment = { horizontal: "center", vertical: "middle" };

      // Add empty row for spacing
      worksheet.addRow([]);

      // Add header row with styling
      const headerRow = worksheet.addRow([
        "Date",
        "Customer",
        "Car",
        "Rental Duration",
        "Total Price",
        "Commission",
        "Status",
      ]);

      // Style header row
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4472C4" }, // Blue background
        };
        cell.font = {
          bold: true,
          color: { argb: "FFFFFF" }, // White text
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Add data rows
      monthlyRentals.forEach((rental, index) => {
        const row = worksheet.addRow([
          moment(rental.createdAt).format("DD/MM/YYYY"),
          `${rental.customer.firstName} ${rental.customer.lastName}`,
          rental.carName,
          `${Math.ceil(
            moment(rental.returnDateTime).diff(
              moment(rental.pickupDateTime),
              "days"
            )
          )} days`,
          rental.totalPrice.toFixed(2),
          (rental.commissionAmount || 0).toFixed(2),
          rental.commissionPaid ? "Paid" : "Unpaid",
        ]);

        if (index % 2 === 1) {
          row.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "E6F0FF" },
            };
          });
        }

        // Center align duration column
        row.getCell(4).alignment = { horizontal: "center" };

        // Right align and format currency columns
        row.getCell(5).alignment = { horizontal: "right" };
        row.getCell(5).numFmt = "€#,##0.00";
        row.getCell(6).alignment = { horizontal: "right" };
        row.getCell(6).numFmt = "€#,##0.00";

        // Center align status
        row.getCell(7).alignment = { horizontal: "center" };

        // Add borders to all cells
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      // Add total row
      const totalRow = worksheet.addRow([
        "",
        "",
        "",
        "TOTAL",
        monthlyRentals
          .reduce((sum, rental) => sum + rental.totalPrice, 0)
          .toFixed(2),
        monthlyRentals
          .reduce((sum, rental) => sum + (rental.commissionAmount || 0), 0)
          .toFixed(2),
        "",
      ]);

      // Style total row
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "double" },
          right: { style: "thin" },
        };

        if (colNumber === 4) {
          cell.alignment = { horizontal: "right" };
        }

        if (colNumber === 5 || colNumber === 6) {
          cell.alignment = { horizontal: "right" };
          cell.numFmt = "€#,##0.00";
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "D9E1F2" },
          };
        }
      });
    } else {
      // TRANSFER REPORT WORKSHEET
      const worksheet = workbook.addWorksheet("Transfer Commission Report");

      // Set column widths for better readability
      worksheet.columns = [
        { header: "Date", key: "date", width: 14 },
        { header: "Client", key: "client", width: 25 },
        { header: "From → To", key: "route", width: 30 },
        { header: "Service Type", key: "serviceType", width: 16 },
        { header: "Total Price", key: "price", width: 14 },
        { header: "Commission", key: "commission", width: 14 },
        { header: "Status", key: "status", width: 10 },
      ];

      // Add title and metadata with styling
      const titleRow = worksheet.addRow([
        `Transfer Commission Report for ${partner.firstName} ${partner.lastName}`,
      ]);
      titleRow.height = 30;
      titleRow.font = { size: 16, bold: true };
      worksheet.mergeCells(`A1:G1`);
      titleRow.alignment = { horizontal: "center", vertical: "middle" };

      const monthRow = worksheet.addRow([
        `Month: ${selectedMonth.format("MMMM YYYY")}`,
      ]);
      monthRow.font = { size: 12, bold: true };
      worksheet.mergeCells(`A2:G2`);
      monthRow.alignment = { horizontal: "center", vertical: "middle" };

      // Add empty row for spacing
      worksheet.addRow([]);

      // Add header row with styling
      const headerRow = worksheet.addRow([
        "Date",
        "Client",
        "From → To",
        "Service Type",
        "Total Price",
        "Commission",
        "Status",
      ]);

      // Style header row
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4472C4" }, // Blue background
        };
        cell.font = {
          bold: true,
          color: { argb: "FFFFFF" }, // White text
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Add data rows
      monthlyTransfers.forEach((transfer, index) => {
        const row = worksheet.addRow([
          moment(transfer.createdAt).format("DD/MM/YYYY"),
          `${transfer.name} ${transfer.surname}`,
          `${transfer.pickupLocation} → ${transfer.dropoffLocation}`,
          transfer.serviceType || "One-way",
          (transfer.priceTotal || 0).toFixed(2),
          (transfer.partnerCommission || 0).toFixed(2),
          transfer.commissionPaid ? "Paid" : "Unpaid",
        ]);

        if (index % 2 === 1) {
          row.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "E6F0FF" },
            };
          });
        }

        // Center align service type column
        row.getCell(4).alignment = { horizontal: "center" };

        // Right align and format currency columns
        row.getCell(5).alignment = { horizontal: "right" };
        row.getCell(5).numFmt = "€#,##0.00";
        row.getCell(6).alignment = { horizontal: "right" };
        row.getCell(6).numFmt = "€#,##0.00";

        // Center align status
        row.getCell(7).alignment = { horizontal: "center" };

        // Add borders to all cells
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      // Add total row
      const totalRow = worksheet.addRow([
        "",
        "",
        "",
        "TOTAL",
        monthlyTransfers
          .reduce((sum, transfer) => sum + (transfer.priceTotal || 0), 0)
          .toFixed(2),
        monthlyTransfers
          .reduce((sum, transfer) => sum + (transfer.partnerCommission || 0), 0)
          .toFixed(2),
        "",
      ]);

      // Style total row
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "double" },
          right: { style: "thin" },
        };

        if (colNumber === 4) {
          cell.alignment = { horizontal: "right" };
        }

        if (colNumber === 5 || colNumber === 6) {
          cell.alignment = { horizontal: "right" };
          cell.numFmt = "€#,##0.00";
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "D9E1F2" },
          };
        }
      });
    }    // Add Yacht Bookings worksheet if needed
    if (activeTab === "yachts") {
      // YACHT BOOKINGS REPORT WORKSHEET
      const worksheet = workbook.addWorksheet("Yacht Booking Commission Report");

      // Set column widths for better readability
      worksheet.columns = [
        { header: "Date", key: "date", width: 14 },
        { header: "Customer", key: "customer", width: 25 },
        { header: "Yacht", key: "yacht", width: 25 },
        { header: "Cruise Type", key: "cruiseType", width: 20 },
        { header: "Booking Date", key: "bookingDate", width: 14 },
        { header: "Total Price", key: "price", width: 14 },
        { header: "Commission", key: "commission", width: 14 },
        { header: "Status", key: "status", width: 10 },
      ];

      // Add title and metadata with styling
      const titleRow = worksheet.addRow([
        `Yacht Booking Commission Report for ${partner.firstName} ${partner.lastName}`,
      ]);
      titleRow.height = 30;
      titleRow.font = { size: 16, bold: true };
      worksheet.mergeCells(`A1:H1`);
      titleRow.alignment = { horizontal: "center", vertical: "middle" };

      const monthRow = worksheet.addRow([
        `Month: ${selectedMonth.format("MMMM YYYY")}`,
      ]);
      monthRow.font = { size: 12, bold: true };
      worksheet.mergeCells(`A2:H2`);
      monthRow.alignment = { horizontal: "center", vertical: "middle" };

      // Add empty row for spacing
      worksheet.addRow([]);

      // Add header row with styling
      const headerRow = worksheet.addRow([
        "Date",
        "Customer",
        "Yacht",
        "Cruise Type",
        "Booking Date",
        "Total Price",
        "Commission",
        "Status",
      ]);

      // Style header row
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4472C4" }, // Blue background
        };
        cell.font = {
          bold: true,
          color: { argb: "FFFFFF" }, // White text
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Add data rows
      monthlyYachtBookings.forEach((booking, index) => {
        const row = worksheet.addRow([
          moment(booking.createdAt).format("DD/MM/YYYY"),
          `${booking.customer?.firstName || ""} ${booking.customer?.lastName || ""}`,
          booking.yachtName,
          booking.cruiseType,
          moment(booking.bookingDate).format("DD/MM/YYYY"),
          (booking.calculatedPrice || 0).toFixed(2),
          (booking.commissionAmount || 0).toFixed(2),
          booking.commissionPaid ? "Paid" : "Unpaid",
        ]);

        if (index % 2 === 1) {
          row.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "E6F0FF" },
            };
          });
        }

        // Center align cruise type column
        row.getCell(4).alignment = { horizontal: "center" };

        // Right align and format currency columns
        row.getCell(6).alignment = { horizontal: "right" };
        row.getCell(6).numFmt = "€#,##0.00";
        row.getCell(7).alignment = { horizontal: "right" };
        row.getCell(7).numFmt = "€#,##0.00";

        // Center align status
        row.getCell(8).alignment = { horizontal: "center" };

        // Add borders to all cells
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      // Add total row
      const totalRow = worksheet.addRow([
        "",
        "",
        "",
        "",
        "TOTAL",
        monthlyYachtBookings
          .reduce((sum, booking) => sum + (booking.calculatedPrice || 0), 0)
          .toFixed(2),
        monthlyYachtBookings
          .reduce((sum, booking) => sum + (booking.commissionAmount || 0), 0)
          .toFixed(2),
        "",
      ]);

      // Style total row
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "double" },
          right: { style: "thin" },
        };

        if (colNumber === 5) {
          cell.alignment = { horizontal: "right" };
        }

        if (colNumber === 6 || colNumber === 7) {
          cell.alignment = { horizontal: "right" };
          cell.numFmt = "€#,##0.00";
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "D9E1F2" },
          };
        }
      });
    }

    // Generate the Excel file and trigger download
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      
      let reportType;
      if (activeTab === "rentals") {
        reportType = "rental";
      } else if (activeTab === "transfers") {
        reportType = "transfer";
      } else {
        reportType = "yacht-booking";
      }
      
      FileSaver.saveAs(
        blob,
        `${reportType}-commission-report-${selectedMonth.format(
          "YYYY-MM"
        )}.xlsx`
      );
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">
            Monthly Commission Report
          </h3>
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FaChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <div className="flex items-center">
              <FaCalendar className="w-4 h-4 text-gray-600 mr-2" />
              <span className="text-sm font-medium">
                {selectedMonth.format("MMMM YYYY")}
              </span>
            </div>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FaChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-4 border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => handleTabChange("yachts")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "yachts"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              } transition-colors duration-150 ease-in-out flex items-center`}
            >
              <FaShip className="mr-2" />
              Yacht Bookings
            </button>
          </div>
        </div>
      </div>{" "}
      <div className="p-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-600 font-medium">
              {activeTab === "rentals" 
                ? "Total Rentals" 
                : activeTab === "transfers"
                ? "Total Transfers"
                : "Total Yacht Bookings"}
            </p>
            <p className="text-2xl font-bold text-blue-900">
              {stats.totalCount}
            </p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-sm text-green-600 font-medium">
              Total Commission
            </p>
            <p className="text-2xl font-bold text-green-900">
              €{stats.totalCommission.toFixed(2)}
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-purple-600 font-medium">
              Total Booking Value
            </p>
            <p className="text-2xl font-bold text-purple-900">
              €{stats.totalBookingValue.toFixed(2)}
            </p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <p className="text-sm text-yellow-600 font-medium">
              Average Commission
            </p>
            <p className="text-2xl font-bold text-yellow-900">
              €{stats.averageCommission.toFixed(2)}
            </p>
          </div>
        </div>{" "}
        {/* Search and Actions Bar */}
        <div className="flex justify-between items-center mb-4">          <div className="relative w-64">
            <input
              type="text"
              placeholder={
                activeTab === "rentals"
                  ? "Search rentals..."
                  : activeTab === "transfers"
                  ? "Search transfers..."
                  : "Search yacht bookings..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <FaSearchengin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          <div className="flex items-center space-x-4">
            {adminToken && (
              <button
                onClick={handlePayAllMonth}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Pay Month
              </button>
            )}
            <button
              onClick={downloadReport}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaDownload className="w-4 h-4 mr-2" />
              Download Report
            </button>
          </div>
        </div>{" "}
        {/* Tables - Conditional rendering based on active tab */}
        {activeTab === "yachts" && (
          /* Yacht Bookings Table */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Yacht
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cruise Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Booking Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {monthlyYachtBookings.map((booking) => (
                  <tr key={booking._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {moment(booking.createdAt).format("DD/MM/YYYY")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.customer?.firstName} {booking.customer?.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.yachtName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.cruiseType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {moment(booking.bookingDate).format("DD/MM/YYYY")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      €{(booking.calculatedPrice || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      €{(booking.commissionAmount || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() =>
                          handleYachtBookingCommissionPaymentToggle(
                            booking._id,
                            booking.commissionPaid
                          )
                        }
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          booking.commissionPaid
                            ? "bg-green-100 text-green-800 hover:bg-green-200"
                            : "bg-red-100 text-red-800 hover:bg-blue-200"
                        }`}
                      >
                        {booking.commissionPaid ? "Paid" : "Unpaid"}
                      </button>
                    </td>
                  </tr>
                ))}
                {monthlyYachtBookings.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No yacht bookings found for this month
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionReport;
